import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import Countdown from '../../../common/components/Countdown/Countdown';
import {
    DRAW_INTERVAL_MS,
    PRIZE_AMOUNT,
    TICKET_PRICE,
    TOTAL_NUMBERS,
} from '../../../core/constants/lottery';
import { HOME_ANCHORS, ROUTES } from '../../../core/constants/routes';
import { formatCount, formatCurrency } from '../../../core/utils/format';
import { useDraws } from '../../../global/contexts/lottery/draws';
import { useTickets } from '../../../global/contexts/lottery/tickets';
import { useReveal } from '../hooks/use-reveal';
import DrawMachine from './machine';

import '../styles/hero.css';

function HeroStat({ label, value, hint }) {
    return (
        <div className="draw-hero__stat">
            <span className="draw-hero__stat-label">{label}</span>
            <strong className="draw-hero__stat-value">{value}</strong>
            {hint && <span className="draw-hero__stat-hint">{hint}</span>}
        </div>
    );
}

export default function DrawHero() {
    const { latest, currentSlot, msToNextDraw, nextDrawAt, advanceDraw } = useDraws();
    const { availableCount } = useTickets();
    const { digits, isSpinning, start } = useReveal(latest.number);

    // Replay the reveal whenever the slot rolls over — either the real
    // 30-minute boundary or the demo's simulate button. Skips first mount so a
    // visitor arriving mid-interval sees the standing result immediately.
    const lastPlayedSlot = useRef(currentSlot);
    useEffect(() => {
        if (lastPlayedSlot.current === currentSlot) return;
        lastPlayedSlot.current = currentSlot;
        start();
    }, [currentSlot, start]);

    return (
        <section className="draw-hero">
            <div className="draw-hero__copy">
                <span className="pill pill--accent">Sorteo cada 30 minutos</span>

                <h1 className="draw-hero__title">
                    Tu suerte cabe en <em>4 dígitos</em>
                </h1>

                <p className="draw-hero__lead">
                    Elige tu combinado entre 0000 y 9999, págalo en segundos y espera la
                    siguiente balotera. Sin esperar al sorteo de la noche.
                </p>

                <div className="draw-hero__actions">
                    <Link className="button button--primary" to={ROUTES.STORE}>
                        Comprar mi número
                    </Link>
                    <a className="button button--ghost" href={`#${HOME_ANCHORS.HISTORY}`}>
                        Ver resultados
                    </a>
                </div>

                <div className="draw-hero__stats">
                    <HeroStat label="Premio mayor" value={formatCurrency(PRIZE_AMOUNT)} hint="por número acertado" />
                    <HeroStat label="Cada número" value={formatCurrency(TICKET_PRICE)} />
                    <HeroStat
                        label="Disponibles"
                        value={formatCount(availableCount)}
                        hint={`de ${formatCount(TOTAL_NUMBERS)}`}
                    />
                </div>
            </div>

            <div className="draw-hero__machine">
                <DrawMachine digits={digits} isSpinning={isSpinning} number={latest.number} />
                <Countdown
                    msRemaining={msToNextDraw}
                    intervalMs={DRAW_INTERVAL_MS}
                    nextAt={nextDrawAt}
                    isDrawing={isSpinning}
                    onSimulate={advanceDraw}
                />
            </div>
        </section>
    );
}
